import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search } from "lucide-react";
import { useUsers } from "../../api/hooks.js";
import {
  Avatar,
  Badge,
  CenteredSpinner,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  table,
} from "../../components/ui.js";
import { UserDetailDrawer } from "./UserDetailDrawer.js";

export function UsersPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [query, setQuery] = useState("");
  const users = useUsers(query);

  const items = users.data?.items ?? [];
  const adminCount = items.filter((u) => u.role === "ADMIN").length;

  return (
    <div className="p-6 lg:p-8">
      <PageHeader
        eyebrow="Access control"
        title="Users"
        subtitle="Accounts, roles and publish / record grants."
        actions={
          <div className="relative w-72 max-w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or username…"
              className="pl-9"
            />
          </div>
        }
      />

      {users.isLoading && <CenteredSpinner label="Loading users…" />}
      {users.error && <ErrorState message={users.error.message} />}

      {users.data && items.length === 0 && (
        <EmptyState title="No users found" />
      )}

      {items.length > 0 && (
        <div className={table.wrap}>
          <div className={table.scroll}>
            <table className={table.table}>
              <thead className={table.head}>
                <tr>
                  <th className={table.th}>Name</th>
                  <th className={table.th}>Role</th>
                  <th className={table.th}>Identities</th>
                  <th className={table.th}>Grants</th>
                </tr>
              </thead>
              <tbody className={table.body}>
                {items.map((u) => (
                  <tr
                    key={u.id}
                    onClick={() => navigate(`/users/${u.id}`)}
                    className={table.row}
                  >
                    <td className={table.td}>
                      <div className="flex items-center gap-3">
                        <Avatar src={u.avatarUrl} name={u.displayName} />
                        <div className="min-w-0">
                          <div className="truncate font-medium text-foreground">
                            {u.displayName}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            @{u.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={table.td}>
                      <Badge tone={u.role === "ADMIN" ? "primary" : "slate"}>
                        {u.role}
                      </Badge>
                    </td>
                    <td className={table.td}>
                      {u.identities.length === 0 ? (
                        <span className="text-muted-foreground/50">—</span>
                      ) : (
                        <span className="text-xs capitalize text-muted-foreground">
                          {u.identities.map((i) => i.provider).join(", ")}
                        </span>
                      )}
                    </td>
                    <td className={table.td}>
                      <span className="tabular-nums text-foreground/80">
                        {u.grantCount}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {id && (
        <UserDetailDrawer
          userId={id}
          adminCount={adminCount}
          onClose={() => navigate("/users")}
        />
      )}
    </div>
  );
}
